import { EventEmitter, Injectable } from "@angular/core";
import { HttpService } from "src/app/services/http.service";
import { environment } from "src/environments/environment";
import { Consultant, CreateCustomerResponse, Culture, Customer, CustomerCanDeleteResponse, CustomerMasterData, Employee, UpdateCustomerResponse, AdminResourceListUpdate, Company, CompanyInfo, Companytemplate, UpdateCompanynameRequest, ImportCustomerSetupResponse, ImportCustomerSetupRequest, GetCustomerEvAdminsResponse, SystemAdminInfo, ProvisioningStatus } from "./customer.model";
import { HttpParamsBuilder } from "src/app/services/HttpParamsBuilder";

@Injectable()
export class CustomerService {

    private readonly apiUrl = `${environment.adminConfig.apiUri}/customer`;

    constructor(
        private http: HttpService
    ) { }

    //#region Azure Customers and storage.
    async validateAzureStorageConnection(connectionString: string): Promise<boolean> {
        const connection = { connectionString: connectionString, containerName: '' };
        return await this.http.post<boolean, any>(`${this.apiUrl}/validateazurestorageconnection`, connection);
    }

    async checkContainerExists(connectionString: string, containerName: string): Promise<boolean> {
        const request = { connectionString: connectionString, containerName: containerName };
        return await this.http.post<boolean, any>(`${this.apiUrl}/checkcontainerexists`, request);
    }

    async createAzureContainer(connectionString: string, containerName: string): Promise<boolean> {
        const request = { connectionString: connectionString, containerName: containerName };
        return await this.http.post<boolean, any>(`${this.apiUrl}/createcontainer`, request);
    } 

    //#region Customer
    async createCustomer(customerrequest: CustomerMasterData): Promise<CreateCustomerResponse> {
        return await this.http.post<CreateCustomerResponse, CustomerMasterData>(`${this.apiUrl}/create`, customerrequest);
    }

    async getCustomer(customerid: number): Promise<CustomerMasterData> {
        return await this.http.get<CustomerMasterData>(`${this.apiUrl}/${customerid}`);
    }

    async updateCustomer(customer: CustomerMasterData): Promise<UpdateCustomerResponse> {
        return await this.http.post<UpdateCustomerResponse, CustomerMasterData>(`${this.apiUrl}/${customer.customerId}/update`, customer);
    }

    async deleteCustomer(customerid: number): Promise<boolean> {
        return await this.http.delete<boolean>(`${this.apiUrl}/${customerid}`);
    }

    //#region Tenant provisioning (async, two-phase)
    async getProvisioningStatus(tenantId: number): Promise<ProvisioningStatus> {
        return await this.http.get<ProvisioningStatus>(`${this.apiUrl}/${tenantId}/provisioning-status`);
    }

    // Phase 2: seed the tenant DB once it is provisioned. Idempotent server-side.
    async completeProvisioning(tenantId: number): Promise<ProvisioningStatus> {
        return await this.http.post<ProvisioningStatus, object>(`${this.apiUrl}/${tenantId}/complete`, {});
    }

    // All not-yet-completed provisionings (customer-list overlay), optionally scoped to a department.
    async getPendingProvisionings(departmentGuid?: string): Promise<ProvisioningStatus[]> {
        const builder = new HttpParamsBuilder();
        builder.append('departmentGuid', departmentGuid);
        return await this.http.get<ProvisioningStatus[]>(`${this.apiUrl}/provisionings`, null, { params: builder.build() });
    }
    //#endregion

    //#region Subqueries

    async updateCustomerAdminResources(request: AdminResourceListUpdate): Promise<string[]> {
        return await this.http.post<string[], AdminResourceListUpdate>(`${this.apiUrl}/${request.customerId}/adminresources`, request);
    }

    async ImportCustomerSetup(customerrequest: ImportCustomerSetupRequest): Promise<ImportCustomerSetupResponse> {
        return await this.http.post<ImportCustomerSetupResponse, ImportCustomerSetupRequest>(`${this.apiUrl}/${customerrequest.targetCustomerId}/import`, customerrequest);
    }

    async canDeleteCustomer(customerid: number): Promise<CustomerCanDeleteResponse> {
        return await this.http.get<CustomerCanDeleteResponse>(`${this.apiUrl}/${customerid}/candelete`);
    }

    async getCompanies(customerId: number): Promise<Company[]> {
        return await this.http.get<Company[]>(`${this.apiUrl}/${customerId}/companies`);
    }

    async getEvAdmins(customerId: number): Promise<GetCustomerEvAdminsResponse> {
        return await this.http.get<GetCustomerEvAdminsResponse>(`${this.apiUrl}/${customerId}/evadmins/`);
    }

    async deleteCompany(customerId: number, companyId: number): Promise<boolean> {
        return await this.http.delete<boolean>(`${this.apiUrl}/${customerId}/company/${companyId}`);
    }

    //#endregion

    //#region Company

    async createCompany(companyinfo: CompanyInfo): Promise<CompanyInfo> {
        return await this.http.post<CompanyInfo, CompanyInfo>(`${this.apiUrl}/company`, companyinfo);
    }

    async updateCompanyname(company: UpdateCompanynameRequest): Promise<boolean> {
        return await this.http.put<boolean, UpdateCompanynameRequest>(`${this.apiUrl}/company/name`, company);
    }

    async getCompanyTemplates(departmentId: string): Promise<Companytemplate[]> {
        return await this.http.get<Companytemplate[]>(`${this.apiUrl}/company/templates/${departmentId}`);
    }

    //#endregion

    async getCustomerList(departmentguid: string, environment?: string): Promise<Customer[]> {
        const builder = new HttpParamsBuilder();
        builder.append('departmentGuid', departmentguid);
        builder.append('environment', environment);
        return await this.http.get<Customer[]>(`${this.apiUrl}/customers`, null, { params: builder.build() });
    }

    async getCustomersForEnvironment(departmentguid: string, environment: string): Promise<Customer[]> {
        const builder = new HttpParamsBuilder();
        builder.append('departmentGuid', departmentguid);
        return await this.http.get<Customer[]>(`${this.apiUrl}/environment/${environment}/customers`, null, { params: builder.build() });
    }

    async getConsultants(departmentguid: string): Promise<Consultant[]> {
        const builder = new HttpParamsBuilder();
        builder.append('departmentGuid', departmentguid);
        return await this.http.get<Consultant[]>(`${this.apiUrl}/consultants`, null, { params: builder.build() });
    }

    async getCultures(): Promise<Culture[]> {
        return await this.http.get<Culture[]>(`${this.apiUrl}/cultures`);
    }

    async getEmployees(excludeDepartmentCode?: string): Promise<Employee[]> {
        const builder = new HttpParamsBuilder();
        builder.append('excludeDepartmentCode', excludeDepartmentCode);

        return await this.http.get<Employee[]>(`${this.apiUrl}/employees`, null, { params: builder.build() });
    }

    async getEmployee(employeeid: string): Promise<Employee> {
        return await this.http.get<Employee>(`${this.apiUrl}/employee/${employeeid}`);
    }

    async getSystemAdminInfo(customerId: number): Promise<SystemAdminInfo[]> {
        return await this.http.get<SystemAdminInfo[]>(`${this.apiUrl}/${customerId}/systemadmininfo`);
    }

}


